import React from 'react';
import { Event } from '../../data/eventsService';

type AlertCardProps = {
  event: Event;
  onClick: () => void;
};

export default function AlertCard({ event, onClick }: AlertCardProps) {
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    let timeAgo = '';
    if (diffMins < 1) {
      timeAgo = 'just now';
    } else if (diffMins < 60) {
      timeAgo = `${diffMins}m ago`;
    } else if (diffHours < 24) {
      timeAgo = `${diffHours}h ago`;
    } else if (diffDays < 7) {
      timeAgo = `${diffDays}d ago`;
    } else {
      timeAgo = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    return {
      timeAgo,
      fullDate: date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  };

  const getAlertConfig = () => {
    switch (event.event_name) {
      case 'session_no_show':
        return {
          title: 'Session No-Show',
          color: 'border-[#8B4513]',
          bgColor: 'bg-[#F5E6D3]',
          textColor: 'text-[#8B4513]',
        };
      case 'seeker_rejected_match':
        return {
          title: 'Seeker Rejected Match',
          color: 'border-[#6B4E71]',
          bgColor: 'bg-[#E8DFF5]',
          textColor: 'text-[#6B4E71]',
        };
      case 'guide_rejected_match':
        return {
          title: 'Guide Rejected Match',
          color: 'border-[#5C4A72]',
          bgColor: 'bg-[#E3D7F0]',
          textColor: 'text-[#5C4A72]',
        };
      case 'seeker_not_matched':
        return {
          title: 'No Match Found',
          color: 'border-[#9B7E46]',
          bgColor: 'bg-[#F7EED9]',
          textColor: 'text-[#9B7E46]',
        };
      case 'rematch_limit_reached':
        return {
          title: 'Rejection Limit Reached',
          color: 'border-[#C41E3A]',
          bgColor: 'bg-[#FFE5E9]',
          textColor: 'text-[#C41E3A]',
        };
      default:
        return {
          title: 'Alert',
          color: 'border-[#2C2731]',
          bgColor: 'bg-[#F2EAC3]',
          textColor: 'text-[#2C2731]',
        };
    }
  };

  const renderContent = () => {
    const { metadata } = event;

    switch (event.event_name) {
      case 'session_no_show':
        return (
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium">{event.user_name || 'Seeker'}</span> missed a{' '}
              <span className="font-medium">{metadata.session_type || 'session'}</span>
            </div>
            {metadata.guide_name && (
              <div className="text-xs text-[#2C2731]/70">Guide: {metadata.guide_name}</div>
            )}
            {metadata.notes && (
              <div className="text-xs text-[#2C2731]/70 italic">Note: {metadata.notes}</div>
            )}
          </div>
        );

      case 'seeker_rejected_match':
        return (
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium">{event.user_name || 'Seeker'}</span> requested a match
              change
            </div>
            {metadata.guide_name && (
              <div className="text-xs text-[#2C2731]/70">
                Previous guide: {metadata.guide_name}
              </div>
            )}
            <div className="flex items-center gap-4 text-xs text-[#2C2731]/70">
              {typeof metadata.seeker_initiated_changes === 'number' && (
                <div>
                  Seeker changes:{' '}
                  <span className="font-medium">{metadata.seeker_initiated_changes}</span>
                </div>
              )}
              {typeof metadata.guide_initiated_changes === 'number' && (
                <div>
                  Guide changes:{' '}
                  <span className="font-medium">{metadata.guide_initiated_changes}</span>
                </div>
              )}
            </div>
            {metadata.note && (
              <div className="text-xs text-[#2C2731]/70 italic">Reason: {metadata.note}</div>
            )}
          </div>
        );

      case 'guide_rejected_match':
        return (
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium">{metadata.guide_name || 'Guide'}</span> requested a
              match change for{' '}
              <span className="font-medium">{event.user_name || 'seeker'}</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-[#2C2731]/70">
              {typeof metadata.seeker_initiated_changes === 'number' && (
                <div>
                  Seeker changes:{' '}
                  <span className="font-medium">{metadata.seeker_initiated_changes}</span>
                </div>
              )}
              {typeof metadata.guide_initiated_changes === 'number' && (
                <div>
                  Guide changes:{' '}
                  <span className="font-medium">{metadata.guide_initiated_changes}</span>
                </div>
              )}
            </div>
            {metadata.note && (
              <div className="text-xs text-[#2C2731]/70 italic">Reason: {metadata.note}</div>
            )}
          </div>
        );

      case 'seeker_not_matched':
        return (
          <div className="space-y-2">
            <div className="text-sm">
              No suitable match found for{' '}
              <span className="font-medium">{event.user_name || 'seeker'}</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-[#2C2731]/70">
              {typeof metadata.total_guides_evaluated === 'number' && (
                <div>
                  Guides evaluated:{' '}
                  <span className="font-medium">{metadata.total_guides_evaluated}</span>
                </div>
              )}
              {typeof metadata.best_score === 'number' && (
                <div>
                  Best score: <span className="font-medium">{metadata.best_score}</span>
                </div>
              )}
            </div>
            {metadata.reason && (
              <div className="text-xs text-[#2C2731]/70 italic">{metadata.reason}</div>
            )}
          </div>
        );

      case 'rematch_limit_reached':
        return (
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium">{event.user_name || 'Seeker'}</span> has reached the
              rejection limit
            </div>
            <div className="flex items-center gap-4 text-xs text-[#2C2731]/70">
              {typeof metadata.seeker_initiated_changes === 'number' && (
                <div>
                  Total rejections:{' '}
                  <span className="font-medium">{metadata.seeker_initiated_changes}</span>
                </div>
              )}
              {typeof metadata.guide_initiated_changes === 'number' && (
                <div>
                  Guide changes:{' '}
                  <span className="font-medium">{metadata.guide_initiated_changes}</span>
                </div>
              )}
            </div>
            {metadata.note && (
              <div className="text-xs text-[#2C2731]/70 italic">Note: {metadata.note}</div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-sm">
            <span className="font-medium">{event.user_name || 'User'}</span>
          </div>
        );
    }
  };

  const config = getAlertConfig();
  const { timeAgo, fullDate } = formatTimestamp(event.timestamp);

  return (
    <div
      onClick={onClick}
      className={`rounded-xl border-l-4 ${config.color} ${config.bgColor} p-5 cursor-pointer hover:shadow-lg transition-all hover:border-l-[6px]`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className={`font-medium ${config.textColor} font-recoleta`}>{config.title}</h3>
              <span className="text-xs text-[#2C2731]/50" title={fullDate}>
                {timeAgo}
              </span>
            </div>
            {renderContent()}
          </div>
        </div>
        <button
          className="text-xs px-3 py-1.5 rounded-lg border border-[#2C2731] bg-[#2C2731] text-[#FEF6D6] hover:bg-[#3C3450] transition-colors font-medium flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          Details
        </button>
      </div>
      {event.user_email && (
        <div className="mt-3 pt-3 border-t border-[#2C2731]/10 text-xs text-[#2C2731]/60">
          {event.user_email}
        </div>
      )}
    </div>
  );
}
